/**
 * useEntitlements - Consolidated entitlements hooks
 * 
 * Includes: base entitlements, exeibit entitlements, sync, feature access
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription, type SubscriptionTier } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';
import {
  type ExeibitPlanId,
  type ExeibitEntitlements as ExeibitEntitlementsType,
  getPlan,
  getEntitlements,
  canStartRun,
  remainingRuns as calcRemainingRuns,
} from "@/lib/billing/entitlements";
import { FEATURE_TIERS, getTierDisplayName, getUpgradeUrl, type FeatureKey } from '@/lib/featureTiers';

// ============================================================
// BASE ENTITLEMENTS
// ============================================================

export type EntitlementStatus = 'pending' | 'active' | 'past_due' | 'canceled' | 'paused';

export interface Entitlement {
  id: string;
  user_id: string;
  purchase_type: string;
  stack_slug: string | null;
  bit_id: string | null;
  status: EntitlementStatus;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  created_at: string;
}

export function useEntitlements() {
  return useQuery({
    queryKey: ['entitlements'],
    queryFn: async (): Promise<Entitlement[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('entitlements')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useEntitlements] Error:', error);
        throw error;
      }

      return (data || []) as Entitlement[];
    },
  });
}

export function deriveAccessStatus(entitlements: Entitlement[]): EntitlementStatus | null {
  if (entitlements.length === 0) return null;
  
  const statusPriority: EntitlementStatus[] = ['active', 'pending', 'past_due', 'paused', 'canceled'];
  
  for (const status of statusPriority) {
    if (entitlements.some(e => e.status === status)) {
      return status;
    }
  }
  
  return entitlements[0]?.status || null;
}

// ============================================================
// EXEIBIT ENTITLEMENTS (inlined from useExeibitEntitlements)
// ============================================================

export interface ExeibitUsage {
  runsStarted: number;
  activeRuns: number;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface UseExeibitEntitlementsResult {
  planId: ExeibitPlanId;
  planLabel: string;
  priceMonthlyEUR: number;
  entitlements: ExeibitEntitlementsType;
  usage: ExeibitUsage;
  remainingRuns: number;
  canStart: boolean;
  limitReason: "limit.runs" | "limit.active" | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useExeibitEntitlements(): UseExeibitEntitlementsResult {
  const { user } = useAuth();
  const [planId, setPlanId] = useState<ExeibitPlanId>("free");
  const [usage, setUsage] = useState<ExeibitUsage>({
    runsStarted: 0,
    activeRuns: 0,
    periodStart: null,
    periodEnd: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setPlanId("free");
      setUsage({ runsStarted: 0, activeRuns: 0, periodStart: null, periodEnd: null });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: subData, error: subError } = await supabase
        .from("exeibit_subscriptions")
        .select("*")
        .eq("owner_id", user.id)
        .maybeSingle();

      if (subError && subError.code !== "PGRST116") {
        throw subError;
      }

      const fetchedPlanId = (subData?.plan_id as ExeibitPlanId) || "free";
      setPlanId(fetchedPlanId);

      const { data: usageData, error: usageError } = await supabase
        .from("exeibit_usage")
        .select("*")
        .eq("owner_id", user.id)
        .maybeSingle();

      if (usageError && usageError.code !== "PGRST116") {
        throw usageError;
      }

      setUsage({
        runsStarted: usageData?.runs_started ?? 0,
        activeRuns: usageData?.active_runs ?? 0,
        periodStart: usageData?.period_start ?? null,
        periodEnd: usageData?.period_end ?? null,
      });
    } catch (err) {
      console.error("Failed to load entitlements:", err);
      setError(err instanceof Error ? err.message : "Failed to load entitlements");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const plan = getPlan(planId);
  const ent = getEntitlements(planId);
  const { allowed: canStart, reason: limitReason } = canStartRun(planId, usage);
  const remainingRuns = calcRemainingRuns(planId, usage.runsStarted);

  return {
    planId,
    planLabel: plan.label,
    priceMonthlyEUR: plan.priceMonthlyEUR,
    entitlements: ent,
    usage,
    remainingRuns,
    canStart,
    limitReason: limitReason || null,
    loading,
    error,
    refresh,
  };
}

// ============================================================
// SYNC ENTITLEMENTS (inlined from useSyncEntitlements)
// ============================================================

interface SyncResult {
  synced: boolean;
  newEntitlements: string[];
  updatedEntitlements: string[];
  message: string;
}

export function useSyncEntitlements() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<SyncResult> => {
      const { data, error } = await supabase.functions.invoke('sync-entitlements');
      
      if (error) {
        throw new Error(error.message || 'Sync failed');
      }
      
      return data as SyncResult;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['user-outputs'] });
      queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      queryClient.invalidateQueries({ queryKey: ['user-stacks'] });
      
      if (result.newEntitlements?.length > 0) {
      }
    },
    onError: (error) => {
      console.error('[useSyncEntitlements] Error:', error);
    },
  });
}

// ============================================================
// FEATURE ACCESS (inlined from useFeatureAccess)
// ============================================================

export interface FeatureAccessResult {
  canAccess: (feature: FeatureKey) => boolean;
  getRequiredTier: (feature: FeatureKey) => SubscriptionTier | null;
  getUpgradeUrl: (feature: FeatureKey) => string;
  getUpgradeMessage: (feature: FeatureKey) => string;
  tier: SubscriptionTier;
  tierDisplayName: string;
  isLoading: boolean;
}

export function useFeatureAccess(): FeatureAccessResult {
  const { tier, loading, hasAccess } = useSubscription();

  return useMemo(() => {
    const canAccessFeature = (feature: FeatureKey): boolean => {
      const requiredTier = FEATURE_TIERS[feature];
      if (requiredTier === null) return true;
      return hasAccess(requiredTier);
    };

    const getRequiredTier = (feature: FeatureKey): SubscriptionTier | null => {
      return FEATURE_TIERS[feature] ?? null;
    };

    const getFeatureUpgradeUrl = (feature: FeatureKey): string => {
      const requiredTier = FEATURE_TIERS[feature];
      return getUpgradeUrl(feature, requiredTier);
    };

    const getUpgradeMessage = (feature: FeatureKey): string => {
      const requiredTier = FEATURE_TIERS[feature];
      if (!requiredTier) return '';
      return `Upgrade naar ${getTierDisplayName(requiredTier)} voor toegang`;
    };

    return {
      canAccess: canAccessFeature,
      getRequiredTier,
      getUpgradeUrl: getFeatureUpgradeUrl,
      getUpgradeMessage,
      tier,
      tierDisplayName: getTierDisplayName(tier),
      isLoading: loading,
    };
  }, [tier, loading, hasAccess]);
}
